import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  ArrowLeft, 
  Building2, 
  Calculator, 
  Users, 
  Target, 
  Mail,
  CheckCircle2,
  Info,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step configuration
const STEPS = [
  { id: 1, title: 'Basisangaben', icon: Building2, description: 'Unternehmensinformationen' },
  { id: 2, title: 'Finanzkennzahlen', icon: Calculator, description: 'Bilanz & Erfolgsrechnung' },
  { id: 3, title: 'Qualitative Faktoren', icon: Users, description: 'Risikobewertung' },
  { id: 4, title: 'Ziel & Kontext', icon: Target, description: 'Bewertungszweck' },
  { id: 5, title: 'Kontaktdaten', icon: Mail, description: 'E-Mail-Zustellung' }
];

// Industries with EBIT multiples
const INDUSTRIES = [
  { value: 'Handel', label: 'Handel', multiple: 7.2 },
  { value: 'Dienstleistung', label: 'Dienstleistung', multiple: 7.7 },
  { value: 'Industrie', label: 'Industrie', multiple: 7.1 },
  { value: 'Handwerk', label: 'Handwerk', multiple: 5.4 },
  { value: 'Chemie', label: 'Chemie', multiple: 5.5 },
  { value: 'Gesundheit', label: 'Gesundheit', multiple: 5.5 },
  { value: 'Logistik', label: 'Logistik', multiple: 5.5 },
  { value: 'Bildung', label: 'Bildung', multiple: 5.5 },
  { value: 'Sonstiges', label: 'Sonstiges', multiple: 5.5 }
];

const PURPOSES = [
  { value: 'Verkauf', label: 'Verkauf des Unternehmens' },
  { value: 'Kauf', label: 'Kauf eines Unternehmens' },
  { value: 'Nachfolge', label: 'Nachfolgeplanung' },
  { value: 'Finanzierung', label: 'Finanzierung' },
  { value: 'Sonstiges', label: 'Sonstiges' }
];

// Risk factor questions
const RISK_FACTORS = [
  { id: 'management_quality', label: 'Mittleres Management vorhanden', helpText: 'Gibt es eine Führungsebene unter der Geschäftsleitung?' },
  { id: 'market_position', label: 'Marktposition & Regionalität', helpText: 'Wie stark ist Ihre regionale Marktstellung?' },
  { id: 'technology', label: 'Technologiestand', helpText: 'Wie modern ist Ihre technische Ausstattung?' },
  { id: 'innovation', label: 'Innovationsfähigkeit', helpText: 'Wie innovativ ist Ihr Unternehmen?' },
  { id: 'sustainability', label: 'Nachhaltigkeit', helpText: 'Wie nachhaltig ist Ihr Geschäftsmodell?' },
  { id: 'pricing_power', label: 'Preissetzungsmacht', helpText: 'Können Sie Preise am Markt durchsetzen?' },
  { id: 'quality', label: 'Produkt-/Dienstleistungsqualität', helpText: 'Wie ist Ihre Qualität im Vergleich zum Wettbewerb?' },
  { id: 'brand', label: 'Markenbekanntheit', helpText: 'Wie bekannt ist Ihre Marke?' },
  { id: 'processes', label: 'Interne Prozesse', helpText: 'Wie gut sind Ihre internen Abläufe strukturiert?' }
];

// Initial form state
const initialFormData = {
  // Step 1: Basic Info
  basic_info: {
    company_name: '',
    industry: '',
    location: '',
    founding_year: '',
    employees: '',
    fiscal_year_end: ''
  },
  // Step 2: Financial Data
  financial_data: {
    liquid_assets: '0',
    current_assets: '0',
    fixed_assets: '0',
    short_term_debt: '0',
    long_term_debt: '0',
    equity: '0',
    net_profit: '0',
    depreciation: '0',
    revenue: '0',
    silent_reserves: '0'
  },
  // Step 3: Qualitative Factors
  qualitative_factors: {
    management_quality: 3,
    market_position: 3,
    technology: 3,
    innovation: 3,
    sustainability: 3,
    pricing_power: 3,
    quality: 3,
    brand: 3,
    processes: 3
  },
  // Step 4: Context
  context: {
    purpose: '',
    timeline: '',
    additional_notes: ''
  },
  // Step 5: Contact
  contact: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_position: '',
    privacy_consent: false,
    marketing_consent: false
  }
};

// Format number for display
const formatCurrency = (value) => {
  if (!value && value !== 0) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('de-CH', { 
    style: 'currency', 
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(num);
};

// Currency input component
const CurrencyInput = ({ value, onChange, placeholder, id, required }) => {
  const [displayValue, setDisplayValue] = useState(value ? formatCurrency(value) : 'CHF 0');
  
  // Update display when value changes from outside
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      setDisplayValue(formatCurrency(value));
    } else {
      setDisplayValue('CHF 0');
    }
  }, [value]);
  
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.-]/g, '');
    setDisplayValue(e.target.value);
    onChange(raw || '0');
  };
  
  const handleBlur = () => {
    if (value) {
      setDisplayValue(formatCurrency(value));
    } else {
      setDisplayValue('CHF 0');
    }
  };
  
  const handleFocus = () => {
    setDisplayValue(value || '0');
  };
  
  return (
    <Input
      id={id}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder || 'CHF 0'}
      required={required}
      className="bg-white"
      data-testid={`input-${id}`}
    />
  );
};

// Progress bar component
const WizardProgress = ({ currentStep, steps }) => {
  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;
  
  return (
    <div className="mb-8">
      <div className="flex justify-between mb-4">
        {steps.map((step) => {
          const Icon = step.icon;
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          
          return (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all
                ${isCompleted ? 'bg-[#FF0000] text-white' : 
                  isActive ? 'bg-[#FF0000] text-white ring-4 ring-[#FFE5E5]' : 
                  'bg-gray-100 text-gray-400'}
              `}>
                {isCompleted ? <CheckCircle2 size={20} /> : <Icon size={20} />}
              </div>
              <span className={`text-xs font-medium text-center hidden sm:block ${isActive ? 'text-[#FF0000]' : 'text-gray-500'}`}>
                {step.title}
              </span>
            </div>
          );
        })}
      </div>
      <Progress value={progress} className="h-2" />
    </div>
  );
};

// Step 1: Basic Info
const Step1BasicInfo = ({ data, updateData }) => {
  const handleChange = (field, value) => {
    updateData('basic_info', { ...data, [field]: value });
  };
  
  return (
    <div className="space-y-6" data-testid="step-1-basic-info">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1a1c1b] mb-2">Basisangaben zum Unternehmen</h2>
        <p className="text-gray-500">Geben Sie die grundlegenden Informationen zu Ihrem Unternehmen ein.</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <Label htmlFor="company_name">Unternehmensname *</Label>
          <Input
            id="company_name"
            value={data.company_name}
            onChange={(e) => handleChange('company_name', e.target.value)}
            placeholder="Muster AG"
            required
            className="bg-white mt-1"
            data-testid="input-company-name"
          />
        </div>
        
        <div>
          <Label htmlFor="industry">Branche *</Label>
          <Select value={data.industry} onValueChange={(v) => handleChange('industry', v)}>
            <SelectTrigger className="bg-white mt-1" data-testid="select-industry">
              <SelectValue placeholder="Branche wählen" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map(ind => (
                <SelectItem key={ind.value} value={ind.value}>
                  {ind.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="location">Standort (Kanton/Stadt) *</Label>
          <Input
            id="location"
            value={data.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="z.B. Zürich"
            required
            className="bg-white mt-1"
            data-testid="input-location"
          />
        </div>
        
        <div>
          <Label htmlFor="founding_year">Gründungsjahr</Label>
          <Input
            id="founding_year"
            type="number"
            value={data.founding_year}
            onChange={(e) => handleChange('founding_year', e.target.value)}
            placeholder="z.B. 2010"
            min="1800"
            max={new Date().getFullYear()}
            className="bg-white mt-1"
            data-testid="input-founding-year"
          />
        </div>
        
        <div>
          <Label htmlFor="employees">Anzahl Mitarbeiter</Label>
          <Input
            id="employees"
            type="number"
            value={data.employees}
            onChange={(e) => handleChange('employees', e.target.value)}
            placeholder="z.B. 25"
            min="0"
            className="bg-white mt-1"
            data-testid="input-employees"
          />
        </div>
      </div>
    </div>
  );
};

// Step 2: Financial Data
const Step2FinancialData = ({ data, updateData }) => {
  const handleChange = (field, value) => {
    updateData('financial_data', { ...data, [field]: value });
  };
  
  return (
    <div className="space-y-8" data-testid="step-2-financial-data">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1a1c1b] mb-2">Finanzkennzahlen</h2>
        <p className="text-gray-500">Geben Sie die wichtigsten Finanzkennzahlen Ihres Unternehmens ein.</p>
      </div>
      
      {/* Balance Sheet - Assets */}
      <div className="bg-gray-50 p-6 rounded-xl">
        <h3 className="font-semibold text-[#1a1c1b] mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-[#FF0000] rounded-full"></span>
          Bilanz – Aktiven
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="liquid_assets">Liquide Mittel *</Label>
            <CurrencyInput
              id="liquid_assets"
              value={data.liquid_assets}
              onChange={(v) => handleChange('liquid_assets', v)}
              placeholder="CHF 0"
              required
            />
          </div>
          <div>
            <Label htmlFor="current_assets">Umlaufvermögen (Rest) *</Label>
            <CurrencyInput
              id="current_assets"
              value={data.current_assets}
              onChange={(v) => handleChange('current_assets', v)}
              placeholder="CHF 0"
              required
            />
          </div>
          <div>
            <Label htmlFor="fixed_assets">Anlagevermögen *</Label>
            <CurrencyInput
              id="fixed_assets"
              value={data.fixed_assets}
              onChange={(v) => handleChange('fixed_assets', v)}
              placeholder="CHF 0"
              required
            />
          </div>
        </div>
      </div>
      
      {/* Balance Sheet - Liabilities */}
      <div className="bg-gray-50 p-6 rounded-xl">
        <h3 className="font-semibold text-[#1a1c1b] mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-[#FF0000] rounded-full"></span>
          Bilanz – Passiven
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="short_term_debt">Kurzfristiges FK *</Label>
            <CurrencyInput
              id="short_term_debt"
              value={data.short_term_debt}
              onChange={(v) => handleChange('short_term_debt', v)}
              placeholder="CHF 0"
              required
            />
          </div>
          <div>
            <Label htmlFor="long_term_debt">Langfristiges FK *</Label>
            <CurrencyInput
              id="long_term_debt"
              value={data.long_term_debt}
              onChange={(v) => handleChange('long_term_debt', v)}
              placeholder="CHF 0"
              required
            />
          </div>
          <div>
            <Label htmlFor="equity">Eigenkapital *</Label>
            <CurrencyInput
              id="equity"
              value={data.equity}
              onChange={(v) => handleChange('equity', v)}
              placeholder="CHF 0"
              required
            />
          </div>
        </div>
      </div>
      
      {/* Income Statement */}
      <div className="bg-gray-50 p-6 rounded-xl">
        <h3 className="font-semibold text-[#1a1c1b] mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-[#FF0000] rounded-full"></span>
          Erfolgsrechnung
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="revenue">Umsatz *</Label>
            <CurrencyInput
              id="revenue"
              value={data.revenue}
              onChange={(v) => handleChange('revenue', v)}
              placeholder="CHF 0"
              required
            />
          </div>
          <div>
            <Label htmlFor="net_profit">Reingewinn (vor Steuern) *</Label>
            <CurrencyInput
              id="net_profit"
              value={data.net_profit}
              onChange={(v) => handleChange('net_profit', v)}
              placeholder="CHF 0"
              required
            />
          </div>
          <div>
            <Label htmlFor="depreciation">Abschreibungen *</Label>
            <CurrencyInput
              id="depreciation"
              value={data.depreciation}
              onChange={(v) => handleChange('depreciation', v)}
              placeholder="CHF 0"
              required
            />
          </div>
        </div>
      </div>
      
      {/* Silent Reserves (optional) */}
      <div className="bg-[#FFE5E5]/30 p-6 rounded-xl border border-[#FF0000]/20">
        <h3 className="font-semibold text-[#1a1c1b] mb-2 flex items-center gap-2">
          <Info size={18} className="text-[#FF0000]" />
          Stille Reserven (optional)
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Falls Vermögenswerte wie Immobilien oder Beteiligungen über dem Buchwert liegen.
        </p>
        <CurrencyInput
          id="silent_reserves"
          value={data.silent_reserves}
          onChange={(v) => handleChange('silent_reserves', v)}
          placeholder="CHF 0"
        />
      </div>
    </div>
  );
};

// Step 3: Qualitative Factors
const Step3QualitativeFactors = ({ data, updateData }) => {
  const handleChange = (field, value) => {
    updateData('qualitative_factors', { ...data, [field]: value[0] });
  };
  
  const getSliderLabel = (value) => {
    const labels = ['Sehr schwach', 'Schwach', 'Durchschnitt', 'Gut', 'Sehr gut'];
    return labels[value - 1] || 'Durchschnitt';
  };
  
  return (
    <div className="space-y-6" data-testid="step-3-qualitative-factors">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1a1c1b] mb-2">Qualitative Faktoren</h2>
        <p className="text-gray-500">Bewerten Sie die folgenden Faktoren auf einer Skala von 1-5.</p>
      </div>
      
      <div className="space-y-8">
        {RISK_FACTORS.map((factor) => (
          <div key={factor.id} className="bg-gray-50 p-5 rounded-xl">
            <div className="flex justify-between items-start mb-4">
              <div>
                <Label className="text-base font-medium">{factor.label}</Label>
                <p className="text-sm text-gray-500 mt-1">{factor.helpText}</p>
              </div>
              <span className={`
                px-3 py-1 rounded-full text-sm font-medium
                ${data[factor.id] >= 4 ? 'bg-green-100 text-green-700' : 
                  data[factor.id] >= 3 ? 'bg-yellow-100 text-yellow-700' : 
                  'bg-red-100 text-red-700'}
              `}>
                {getSliderLabel(data[factor.id])}
              </span>
            </div>
            <Slider
              value={[data[factor.id]]}
              onValueChange={(v) => handleChange(factor.id, v)}
              min={1}
              max={5}
              step={1}
              className="w-full"
              data-testid={`slider-${factor.id}`}
            />
            <div className="flex justify-between mt-2 text-xs text-gray-400">
              <span>1 - Schwach</span>
              <span>5 - Sehr gut</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Step 4: Valuation Context
const Step4Context = ({ data, updateData }) => {
  const handleChange = (field, value) => {
    updateData('context', { ...data, [field]: value });
  };
  
  return (
    <div className="space-y-6" data-testid="step-4-context">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1a1c1b] mb-2">Ziel & Kontext</h2>
        <p className="text-gray-500">Für welchen Zweck benötigen Sie die Bewertung?</p>
      </div>
      
      <div className="space-y-6">
        <div>
          <Label htmlFor="purpose">Bewertungszweck *</Label>
          <Select value={data.purpose} onValueChange={(v) => handleChange('purpose', v)}>
            <SelectTrigger className="bg-white mt-1" data-testid="select-purpose">
              <SelectValue placeholder="Zweck auswählen" />
            </SelectTrigger>
            <SelectContent>
              {PURPOSES.map(p => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="timeline">Geplanter Zeithorizont</Label>
          <Select value={data.timeline} onValueChange={(v) => handleChange('timeline', v)}>
            <SelectTrigger className="bg-white mt-1" data-testid="select-timeline">
              <SelectValue placeholder="Zeithorizont auswählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sofort">Sofort / in den nächsten Wochen</SelectItem>
              <SelectItem value="3-monate">Innerhalb von 3 Monaten</SelectItem>
              <SelectItem value="6-monate">Innerhalb von 6 Monaten</SelectItem>
              <SelectItem value="1-jahr">Innerhalb eines Jahres</SelectItem>
              <SelectItem value="unbestimmt">Noch unbestimmt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="additional_notes">Zusätzliche Anmerkungen</Label>
          <Textarea
            id="additional_notes"
            value={data.additional_notes}
            onChange={(e) => handleChange('additional_notes', e.target.value)}
            placeholder="Gibt es besondere Umstände oder Informationen, die wir berücksichtigen sollten?"
            className="bg-white mt-1 min-h-[120px]"
            data-testid="textarea-notes"
          />
        </div>
      </div>
    </div>
  );
};

// Step 5: Contact Info
const Step5Contact = ({ data, updateData }) => {
  const handleChange = (field, value) => {
    updateData('contact', { ...data, [field]: value });
  };
  
  return (
    <div className="space-y-6" data-testid="step-5-contact">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#1a1c1b] mb-2">Kontaktdaten</h2>
        <p className="text-gray-500">Ihre Bewertung wird an diese E-Mail-Adresse gesendet.</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="first_name">Vorname *</Label>
          <Input
            id="first_name"
            value={data.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            placeholder="Max"
            required
            className="bg-white mt-1"
            data-testid="input-first-name"
          />
        </div>
        
        <div>
          <Label htmlFor="last_name">Nachname *</Label>
          <Input
            id="last_name"
            value={data.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            placeholder="Muster"
            required
            className="bg-white mt-1"
            data-testid="input-last-name"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label htmlFor="email">E-Mail-Adresse *</Label>
          <Input
            id="email"
            type="email"
            value={data.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="max.muster@beispiel.ch"
            required
            className="bg-white mt-1"
            data-testid="input-email"
          />
        </div>
        
        <div>
          <Label htmlFor="phone">Telefon <span className="text-red-500">*</span></Label>
          <Input
            id="phone"
            type="tel"
            value={data.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="+41 79 123 45 67"
            className="bg-white mt-1"
            data-testid="input-phone"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="company_position">Position im Unternehmen</Label>
          <Input
            id="company_position"
            value={data.company_position}
            onChange={(e) => handleChange('company_position', e.target.value)}
            placeholder="z.B. Geschäftsführer"
            className="bg-white mt-1"
            data-testid="input-position"
          />
        </div>
      </div>
      
      {/* Consent checkboxes */}
      <div className="space-y-4 mt-8 pt-6 border-t">
        <div className="flex items-start gap-3">
          <Checkbox
            id="privacy_consent"
            checked={data.privacy_consent}
            onCheckedChange={(checked) => handleChange('privacy_consent', checked)}
            data-testid="checkbox-privacy"
          />
          <div>
            <Label htmlFor="privacy_consent" className="cursor-pointer">
              Ich akzeptiere die{' '}
              <a href="/datenschutz" target="_blank" className="text-[#FF0000] hover:underline">
                Datenschutzerklärung
              </a>{' '}
              und stimme der Verarbeitung meiner Daten zu. *
            </Label>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <Checkbox
            id="marketing_consent"
            checked={data.marketing_consent}
            onCheckedChange={(checked) => handleChange('marketing_consent', checked)}
            data-testid="checkbox-marketing"
          />
          <div>
            <Label htmlFor="marketing_consent" className="cursor-pointer text-gray-600">
              Ich möchte gelegentlich über Neuigkeiten und Angebote informiert werden.
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};

// Format currency for display
const formatCHF = (value) => {
  if (value >= 1000000) {
    return `CHF ${(value / 1000000).toFixed(2)} Mio.`;
  }
  return new Intl.NumberFormat('de-CH', { 
    style: 'currency', 
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(value);
};

// Success Screen with Results
const SuccessScreen = ({ companyName, result, aiCommentary }) => {
  const navigate = useNavigate();
  
  const hasAI = aiCommentary?.enabled && aiCommentary?.summary;
  
  // Display range: value_mid (lower bound) to value_high (upper bound)
  const range = { low: result?.value_mid, high: result?.value_high };
  
  return (
    <div className="py-8" data-testid="success-screen">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#1a1c1b] mb-2">
          Ihre Bewertung ist fertig!
        </h2>
        <p className="text-gray-500">
          Unternehmensbewertung für <strong>{companyName}</strong>
        </p>
      </div>
      
      {/* Value Box */}
      {result && (
        <div className="bg-gradient-to-br from-[#1a1c1b] to-[#333] text-white p-8 rounded-2xl mb-8">
          <p className="text-center text-sm text-gray-400 uppercase tracking-wider mb-2">
            Indikative Wertbandbreite
          </p>
          <p className="text-center text-3xl md:text-4xl font-bold text-[#FF0000] mb-4">
            {formatCHF(range.low)} – {formatCHF(range.high)}
          </p>
          
          {/* Method breakdown - BLURRED */}
          {result.methods && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <p className="text-xs text-gray-500 uppercase mb-3">Bewertungsmethoden</p>
              <div className="grid grid-cols-2 gap-3 text-sm relative">
                <div className="flex justify-between">
                  <span className="text-gray-400">Ertragswert</span>
                  <span className="blur-sm select-none">{formatCHF(result.methods.earnings_value)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">EBIT-Multiple</span>
                  <span className="blur-sm select-none">{formatCHF(result.methods.ebit_multiple)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Substanzwert</span>
                  <span className="blur-sm select-none">{formatCHF(result.methods.asset_value)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Praktikermethode</span>
                  <span className="blur-sm select-none">{formatCHF(result.methods.practitioner_value)}</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-4">
                Detaillierte Werte in der professionellen Bewertung
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* AI Commentary */}
      {hasAI && (
        <div className="space-y-4 mb-8">
          {/* Summary */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-blue-900 mb-1">KI-Analyse</p>
                <p className="text-blue-800 text-sm">{aiCommentary.summary}</p>
              </div>
            </div>
          </div>
          
          {/* Strengths & Risks */}
          <div className="grid md:grid-cols-2 gap-4">
            {aiCommentary.strengths?.length > 0 && (
              <div className="bg-green-50 border border-green-100 rounded-xl p-5">
                <p className="font-medium text-green-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-green-600" />
                  Stärken
                </p>
                <ul className="space-y-2">
                  {aiCommentary.strengths.map((s) => (
                    <li key={s} className="text-sm text-green-800 flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {aiCommentary.risks?.length > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
                <p className="font-medium text-amber-900 mb-3 flex items-center gap-2">
                  <Info size={18} className="text-amber-600" />
                  Risiken
                </p>
                <ul className="space-y-2">
                  {aiCommentary.risks.map((r) => (
                    <li key={r} className="text-sm text-amber-800 flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* Recommendations */}
          {aiCommentary.recommendations?.length > 0 && (
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-5">
              <p className="font-medium text-purple-900 mb-3">Empfehlungen</p>
              <ul className="space-y-2">
                {aiCommentary.recommendations.map((r) => (
                  <li key={r} className="text-sm text-purple-800 flex items-start gap-2">
                    <ArrowRight size={14} className="text-purple-500 mt-1 flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* Email notice */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center">
        <p className="text-sm text-gray-600">
          Die detaillierte Bewertung wird Ihnen per E-Mail zugestellt.
          Bitte überprüfen Sie auch Ihren Spam-Ordner.
        </p>
      </div>
      
      {/* Disclaimer */}
      <div className="bg-[#FFE5E5]/50 border border-[#FF0000]/20 rounded-xl p-4 mb-8">
        <p className="text-xs text-gray-600">
          <strong>Hinweis:</strong> Diese Bewertung ist eine indikative Schätzung basierend auf Ihren Angaben. 
          Für eine bankentaugliche, rechtlich fundierte Bewertung empfehlen wir eine professionelle 
          Unternehmensbewertung durch unsere zertifizierten Experten.
        </p>
      </div>
      
      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button 
          onClick={() => navigate('/')}
          variant="outline"
          className="border-[#FF0000] text-[#FF0000] hover:bg-[#FF0000] hover:text-white"
          data-testid="btn-back-home"
        >
          Zur Startseite
        </Button>
        <Button 
          onClick={() => navigate('/kontakt')}
          className="bg-[#FF0000] hover:bg-[#CC0000] text-white"
          data-testid="btn-contact"
        >
          Professionelle Beratung anfragen
        </Button>
      </div>
    </div>
  );
};

// Main Wizard Component
const BewertungPage = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [valuationResult, setValuationResult] = useState(null);
  const [aiCommentary, setAiCommentary] = useState(null);
  
  // Load URL parameters on mount
  useEffect(() => {
    const industry = searchParams.get('industry');
    const revenue = searchParams.get('revenue');
    const employees = searchParams.get('employees');
    const foundingYear = searchParams.get('founding_year');
    
    if (industry || revenue || employees || foundingYear) {
      setFormData(prev => ({
        ...prev,
        basic_info: {
          ...prev.basic_info,
          industry: industry || prev.basic_info.industry,
          employees: employees || prev.basic_info.employees,
          founding_year: foundingYear || prev.basic_info.founding_year
        },
        financial_data: {
          ...prev.financial_data,
          revenue: revenue || prev.financial_data.revenue
        }
      }));
    }
  }, [searchParams]);
  
  const updateData = useCallback((section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  }, []);
  
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.basic_info.company_name && 
               formData.basic_info.industry && 
               formData.basic_info.location;
      case 2:
        const f = formData.financial_data;
        return f.liquid_assets !== '' && 
               f.current_assets !== '' && 
               f.fixed_assets !== '' &&
               f.short_term_debt !== '' && 
               f.long_term_debt !== '' && 
               f.equity !== '' &&
               f.net_profit !== '' && 
               f.depreciation !== '' && 
               f.revenue !== '';
      case 3:
        return true; // Sliders have default values
      case 4:
        return formData.context.purpose;
      case 5:
        return formData.contact.first_name && 
               formData.contact.last_name && 
               formData.contact.email && 
               formData.contact.phone &&
               formData.contact.privacy_consent;
      default:
        return false;
    }
  };
  
  const handleNext = () => {
    if (!validateStep(currentStep)) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    
    if (currentStep < STEPS.length) {
      // GA4: Track valuation_started when moving from step 1 to step 2
      if (currentStep === 1 && typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'valuation_started', {
          event_category: 'valuation'
        });
      }
      
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare payload - convert string numbers to actual numbers
      const payload = {
        basic_info: {
          company_name: formData.basic_info.company_name,
          industry: formData.basic_info.industry,
          location: formData.basic_info.location,
          founding_year: formData.basic_info.founding_year ? parseInt(formData.basic_info.founding_year) : null,
          employees: formData.basic_info.employees ? parseInt(formData.basic_info.employees) : null,
          fiscal_year_end: formData.basic_info.fiscal_year_end || null
        },
        financial_data: {
          liquid_assets: parseFloat(formData.financial_data.liquid_assets) || 0,
          current_assets: parseFloat(formData.financial_data.current_assets) || 0,
          fixed_assets: parseFloat(formData.financial_data.fixed_assets) || 0,
          short_term_debt: parseFloat(formData.financial_data.short_term_debt) || 0,
          long_term_debt: parseFloat(formData.financial_data.long_term_debt) || 0,
          equity: parseFloat(formData.financial_data.equity) || 0,
          net_profit: parseFloat(formData.financial_data.net_profit) || 0,
          depreciation: parseFloat(formData.financial_data.depreciation) || 0,
          revenue: parseFloat(formData.financial_data.revenue) || 0,
          silent_reserves: parseFloat(formData.financial_data.silent_reserves) || 0
        },
        qualitative_factors: formData.qualitative_factors,
        context: {
          purpose: formData.context.purpose,
          timeline: formData.context.timeline || null,
          additional_notes: formData.context.additional_notes || null
        },
        contact: {
          first_name: formData.contact.first_name,
          last_name: formData.contact.last_name,
          email: formData.contact.email,
          phone: formData.contact.phone || null,
          company_position: formData.contact.company_position || null,
          privacy_consent: formData.contact.privacy_consent,
          marketing_consent: formData.contact.marketing_consent
        }
      };
      
      const response = await fetch(`${API_URL}/api/valuation/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler bei der Bewertung');
      }
      
      const result = await response.json();
      
      if (result.success) {
        // Store results for display
        setValuationResult(result.result_summary);
        setAiCommentary(result.ai_commentary);
        setIsComplete(true);
        toast.success('Ihre Bewertung wurde erfolgreich erstellt!');
        
        // GA4: Track valuation_completed after successful submission
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'valuation_completed', {
            event_category: 'valuation',
            branche: formData.basic_info.industry || undefined,
            unternehmensgroesse: formData.basic_info.employees || undefined
          });
        }
      } else {
        throw new Error(result.message || 'Unbekannter Fehler');
      }
      
    } catch (error) {
      toast.error(error.message || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1BasicInfo data={formData.basic_info} updateData={updateData} />;
      case 2:
        return <Step2FinancialData data={formData.financial_data} updateData={updateData} />;
      case 3:
        return <Step3QualitativeFactors data={formData.qualitative_factors} updateData={updateData} />;
      case 4:
        return <Step4Context data={formData.context} updateData={updateData} />;
      case 5:
        return <Step5Contact data={formData.contact} updateData={updateData} />;
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#f8f9fa] relative overflow-hidden">
      {/* Decorative Arrows */}
      <img 
        src="/arrow-down.webp" 
        alt="" 
        className="fixed top-32 right-8 w-16 h-auto opacity-10 pointer-events-none hidden xl:block"
        aria-hidden="true"
      />
      <img 
        src="/arrow-right.webp" 
        alt="" 
        className="fixed bottom-32 left-8 w-20 h-auto opacity-10 pointer-events-none hidden xl:block"
        aria-hidden="true"
      />
      
      <Header />
      
      <main className="pt-24 pb-16 relative z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {!isComplete ? (
            <>
              {/* Progress */}
              <WizardProgress currentStep={currentStep} steps={STEPS} />
              
              {/* Form Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-10">
                {renderStep()}
                
                {/* Navigation */}
                <div className="flex justify-between mt-10 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={currentStep === 1}
                    className="gap-2"
                    data-testid="btn-prev"
                  >
                    <ArrowLeft size={18} />
                    Zurück
                  </Button>
                  
                  {currentStep < STEPS.length ? (
                    <Button
                      onClick={handleNext}
                      className="bg-[#FF0000] hover:bg-[#CC0000] text-white gap-2"
                      data-testid="btn-next"
                    >
                      Weiter
                      <ArrowRight size={18} />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="bg-[#FF0000] hover:bg-[#CC0000] text-white gap-2 min-w-[180px]"
                      data-testid="btn-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Wird berechnet...
                        </>
                      ) : (
                        <>
                          Bewertung anfordern
                          <ArrowRight size={18} />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Help text */}
              <p className="text-center text-sm text-gray-500 mt-6">
                Alle mit * gekennzeichneten Felder sind Pflichtfelder.
                <br />
                Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
              </p>
            </>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-10">
              <SuccessScreen 
                companyName={formData.basic_info.company_name} 
                result={valuationResult}
                aiCommentary={aiCommentary}
              />
            </div>
          )}
          
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BewertungPage;
